import { format as fmt, isValid, parse, parseISO } from 'date-fns';
import { CalendarDays, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { cn } from '../lib/cn';
import { Input, useControlProps } from './field';
import { Popover, PopoverContent, PopoverTrigger } from './overlay';

export interface DatePickerProps {
  /** ISO date (yyyy-MM-dd) or null. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Display/parse format (organization setting), default dd/MM/yyyy. */
  format?: string;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  /**
   * Typed text that cannot be parsed as a date is never turned into a value (`onChange` is not called), so it never overwrites
   * `value` and would otherwise be lost silently if the form saves anyway. Pass this to refuse saving until it is fixed or cleared.
   */
  onValidityChange?: (invalid: boolean) => void;
  /** Choosing month and year from dropdowns, so a far-off date needs no repeated clicking through months. On by default; `false` for a field that only ever needs a nearby date. */
  years?: boolean;
  /** A date is short, so the field is a fixed, compact width (`w-56`) by default; pass a width class here to widen or narrow it. */
  className?: string;
}

const toIso = (d: Date) => fmt(d, 'yyyy-MM-dd');
const fromIso = (s: string | null): Date | undefined => {
  if (!s) return undefined;
  const d = parseISO(s);
  return isValid(d) ? d : undefined;
};

/**
 * Text input that accepts typing in the organization's date format, plus a calendar popover.
 * Typed input is validated on blur and never silently discarded.
 */
export function DatePicker({
  value,
  onChange,
  format = 'dd/MM/yyyy',
  minDate,
  maxDate,
  placeholder,
  disabled,
  invalid,
  id,
  years = true,
  className,
  onValidityChange,
}: DatePickerProps) {
  const control = useControlProps({ id, invalid });
  const selected = fromIso(value);
  const [text, setText] = useState(selected ? fmt(selected, format) : '');
  const [open, setOpen] = useState(false);
  const [parseError, setParseError] = useState(false);

  function markError(next: boolean) {
    setParseError(next);
    onValidityChange?.(next);
  }

  useEffect(() => {
    setText(selected ? fmt(selected, format) : '');
    markError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, format]);

  const min = fromIso(minDate ?? null);
  const max = fromIso(maxDate ?? null);

  function commit() {
    const trimmed = text.trim();
    if (!trimmed) {
      markError(false);
      if (value !== null) onChange(null);
      return;
    }
    const parsed = parse(trimmed, format, new Date());
    if (!isValid(parsed) || (min && parsed < min) || (max && parsed > max)) {
      markError(true);
      return;
    }
    markError(false);
    onChange(toIso(parsed));
  }

  const now = new Date();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Input
        id={control.id}
        value={text}
        disabled={disabled}
        invalid={control.invalid || parseError}
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder ?? format.toLowerCase()}
        aria-describedby={control['aria-describedby']}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'ArrowDown' && e.altKey) setOpen(true);
        }}
        wrapperClassName={cn('w-full max-w-full sm:w-56', className)}
        trailing={
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={selected ? `Change date, currently ${fmt(selected, format)}` : 'Choose date'}
              className="-mr-1.5 grid size-7 place-items-center rounded text-subtle hover:bg-sunken hover:text-fg disabled:opacity-50"
            >
              <CalendarDays className="size-4" aria-hidden />
            </button>
          </PopoverTrigger>
        }
      />
      {parseError ? (
        <p role="alert" className="mt-1.5 text-sm text-danger-fg">
          Enter a valid date as {format.toUpperCase()}
          {min || max ? ' within the allowed range' : ''}.
        </p>
      ) : null}
      <PopoverContent align="end" className="p-2">
        <DayPicker
          mode="single"
          selected={selected}
          defaultMonth={selected ?? max ?? now}
          onSelect={(d) => {
            if (d) {
              onChange(toIso(d));
              setOpen(false);
            }
          }}
          disabled={[...(min ? [{ before: min }] : []), ...(max ? [{ after: max }] : [])]}
          captionLayout={years ? 'dropdown' : 'label'}
          startMonth={min ?? new Date(now.getFullYear() - 90, 0)}
          endMonth={max ?? new Date(now.getFullYear() + 5, 11)}
          weekStartsOn={1}
          showOutsideDays
          classNames={{
            root: 'p-1',
            months: 'relative',
            month_caption: 'flex h-9 items-center justify-center',
            caption_label: 'text-sm font-semibold text-fg',
            dropdowns: 'flex items-center gap-1.5 text-sm font-medium',
            dropdown_root: 'relative rounded-md border border-border-strong px-2 py-1 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
            dropdown: 'absolute inset-0 w-full cursor-pointer opacity-0',
            nav: 'absolute inset-x-0 top-0 flex h-9 items-center justify-between',
            button_previous: 'grid size-8 place-items-center rounded-md text-muted hover:bg-sunken hover:text-fg',
            button_next: 'grid size-8 place-items-center rounded-md text-muted hover:bg-sunken hover:text-fg',
            month_grid: 'mt-1 border-collapse',
            weekdays: '',
            weekday: 'size-9 text-center text-xs font-medium text-subtle',
            week: '',
            day: 'p-0 text-center',
            day_button: cn(
              'size-9 rounded-md text-sm tabular-nums text-fg transition-colors hover:bg-sunken',
              'in-data-[selected=true]:bg-brand in-data-[selected=true]:font-semibold in-data-[selected=true]:text-on-brand in-data-[selected=true]:hover:bg-brand-hover',
              'in-data-[today=true]:font-semibold in-data-[today=true]:ring-1 in-data-[today=true]:ring-inset in-data-[today=true]:ring-brand-border',
              'in-data-[outside=true]:text-disabled in-data-[disabled=true]:cursor-not-allowed in-data-[disabled=true]:text-disabled in-data-[disabled=true]:hover:bg-transparent',
            ),
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Parses what people actually type into a 24-hour "HH:mm" string: "1030", "10:30", "10.30", "9", "9pm", "9:05 PM".
 * Returns null when the text is not a valid time.
 */
export function parseTime(text: string): string | null {
  const m = text.trim().toLowerCase().match(/^(\d{1,2})(?:[:.]?(\d{2}))?\s*(am|pm)?$/);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] === undefined ? 0 : Number(m[2]);
  const suffix = m[3];
  if (min > 59) return null;
  if (suffix) {
    if (h < 1 || h > 12) return null;
    if (suffix === 'pm' && h < 12) h += 12;
    if (suffix === 'am' && h === 12) h = 0;
  } else if (h > 23) {
    return null;
  }
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** "HH:mm" -> display text in the organization's time format. */
export function formatTime(value: string, format: '24h' | '12h'): string {
  if (!/^\d{2}:\d{2}$/.test(value)) return '';
  if (format === '24h') return value;
  const h = Number(value.slice(0, 2));
  return `${h % 12 === 0 ? 12 : h % 12}:${value.slice(3)} ${h < 12 ? 'AM' : 'PM'}`;
}

/**
 * Time entry that follows the organization's 12/24-hour setting (a native time input would follow the
 * operating-system setting instead). Value is always 24-hour "HH:mm". Typed input is validated on blur.
 */
export function TimeField({
  value,
  onChange,
  format = '24h',
  disabled,
  invalid,
  id,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  format?: '24h' | '12h';
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  className?: string;
}) {
  const control = useControlProps({ id, invalid });
  const [text, setText] = useState(formatTime(value, format));
  const [bad, setBad] = useState(false);

  useEffect(() => {
    setText(formatTime(value, format));
    setBad(false);
  }, [value, format]);

  function commit() {
    if (!text.trim()) {
      setBad(false);
      if (value) onChange('');
      return;
    }
    const parsed = parseTime(text);
    if (parsed === null) return setBad(true);
    setBad(false);
    onChange(parsed);
    setText(formatTime(parsed, format));
  }

  return (
    <>
      <Input
        id={control.id}
        value={text}
        disabled={disabled}
        invalid={control.invalid || bad}
        inputMode="numeric"
        autoComplete="off"
        placeholder={format === '24h' ? 'HH:MM' : 'h:mm AM'}
        aria-describedby={control['aria-describedby']}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && commit()}
        trailing={<Clock className="size-4" aria-hidden />}
        wrapperClassName={className}
      />
      {bad ? (
        <p role="alert" className="mt-1.5 text-sm text-danger-fg">
          Enter a time such as {format === '24h' ? '14:30' : '2:30 PM'}.
        </p>
      ) : null}
    </>
  );
}
